import { Injectable } from '@angular/core';
import { AbstractControl, FormArray } from '@angular/forms';

/** Quantities of a hole as loaded with the delivery (same field names as the hole form). */
export interface HoleQuantitiesBaseline {
  cartdridge?: number | null;
  booster?: number | null;
  detos?: { count?: number | null }[] | null;
}

/** True when `value` is empty or a number with at most `decimals` decimals (float noise ignored). */
function hasAtMostDecimals(value: unknown, decimals: number): boolean {
  if (value === null || value === undefined || value === '') return true;
  const scaled = Number(value) * 10 ** decimals;
  return Number.isFinite(scaled) && Math.abs(scaled - Math.round(scaled)) < 1e-6;
}

/** True when both quantities are the same number, or both empty. */
function isSameQuantity(value: unknown, initial: unknown): boolean {
  const isEmpty = (v: unknown) => v === null || v === undefined || v === '';
  if (isEmpty(value) || isEmpty(initial)) return isEmpty(value) && isEmpty(initial);
  return Number(value) === Number(initial);
}

/**
 * Quantity rules of a hole delivery, shared by the hole editor (per hole) and the delivery modal
 * (every submitted hole, whatever the tab used to save):
 * boosters and detonators are whole numbers, cartridges take at most one decimal.
 */
@Injectable({ providedIn: 'root' })
export class HoleQuantityValidationService {

  /** Quantity inputs whose text is not a number yet (e.g. "." or "1."): they leave null in their control. */
  private readonly incompleteQuantities = new WeakSet<AbstractControl>();

  markIncomplete(control: AbstractControl): void {
    this.incompleteQuantities.add(control);
  }

  markComplete(control: AbstractControl): void {
    this.incompleteQuantities.delete(control);
  }

  /**
   * Only quantities that differ from the loaded value are checked: a stored value (e.g. an old 1.25)
   * stays valid. An unfinished entry is always refused, so it is never saved as a cleared quantity.
   */
  isHoleValid(hole: AbstractControl, baseline: HoleQuantitiesBaseline | null | undefined): boolean {
    const detos = (hole.get('detos') as FormArray | null)?.controls ?? [];
    const cartridge = hole.get('cartdridge');
    const booster = hole.get('booster');
    const counts = detos.map(deto => deto.get('count'));

    if ([cartridge, booster, ...counts].some(control => control && this.incompleteQuantities.has(control))) {
      return false;
    }

    const isChanged = (control: AbstractControl | null, initialValue: unknown): boolean =>
      !!control && !isSameQuantity(control.value, initialValue);

    if (isChanged(cartridge, baseline?.cartdridge) && !hasAtMostDecimals(cartridge!.value, 1)) return false;
    if (isChanged(booster, baseline?.booster) && !hasAtMostDecimals(booster!.value, 0)) return false;

    return counts.every((count, j) =>
      !isChanged(count, baseline?.detos?.[j]?.count) || hasAtMostDecimals(count!.value, 0));
  }
}
